import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Equal, TrendingDown, TrendingUp } from 'lucide-react';
import Text from 'components/_UI/Text';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import currency from 'helpers/currency';

const OUTCOMES = {
    profit: { label: 'Lucro', color: colors.successDark, Icon: TrendingUp },
    loss: { label: 'Prejuízo', color: colors.dangerDark, Icon: TrendingDown },
    break_even: { label: 'Sem lucro nem prejuízo', color: colors.coralGrey, Icon: Equal },
};

const percent = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

const toNumber = (value) => Number(value) || 0;

const barWidth = (value, biggest) => {
    if (value <= 0 || biggest <= 0) return 0;

    return Math.max((value / biggest) * 100, 2);
};

/**
 * Relatório de despesas x lucro do evento. A receita já vem sem as taxas
 * (taxa de serviço e juros), e orçamentos não contam como gasto.
 *
 * `result` chega nulo para quem não pode ver as vendas do evento, e ausente
 * se a API ainda não foi atualizada: nos dois casos o cartão não aparece.
 */
export default function EventResultCard({ result, budgetExpenses }) {
    if (!result) return null;

    const outcome = OUTCOMES[result.outcome] || OUTCOMES.break_even;
    const { Icon } = outcome;

    const paid = toNumber(result.revenue?.paid);
    const fees = toNumber(result.revenue?.fees);
    const offline = toNumber(result.revenue?.offline);
    const revenue = toNumber(result.revenue?.total);
    const expenses = toNumber(result.expenses);
    const balance = toNumber(result.balance);
    const budget = toNumber(budgetExpenses);
    const hasMargin = result.margin !== null && result.margin !== undefined;
    const biggest = Math.max(revenue, expenses);

    return (
        <Card>
            <Column>
                <Text name='subtitle' color={colors.nightDark} paddingBottom={0}>
                    Resultado do evento
                </Text>
                <Text>Receita líquida, já sem as taxas, menos as despesas</Text>
            </Column>

            <Content>
                <Column>
                    <Headline color={outcome.color}>
                        <Icon size={20} color={outcome.color} />
                        <Text color={outcome.color} type='bold' paddingBottom={0} marginLeft={'8px'}>
                            {outcome.label}
                        </Text>
                    </Headline>

                    {result.outcome !== 'break_even' && (
                        <Amount color={outcome.color}>{currency.format(Math.abs(balance))}</Amount>
                    )}

                    {hasMargin && (
                        <Text>Margem de {percent.format(result.margin)}% sobre a receita</Text>
                    )}

                    <Bars>
                        <BarLine>
                            <BarLabel>
                                <Text paddingBottom={0} color={colors.nightDark}>Receita líquida</Text>
                                <Text paddingBottom={0} color={colors.nightDark} type='bold'>
                                    {currency.format(revenue)}
                                </Text>
                            </BarLabel>
                            <BarTrack>
                                <BarFill color={colors.successDark} width={barWidth(revenue, biggest)} />
                            </BarTrack>
                        </BarLine>

                        <BarLine>
                            <BarLabel>
                                <Text paddingBottom={0} color={colors.nightDark}>Despesas</Text>
                                <Text paddingBottom={0} color={colors.nightDark} type='bold'>
                                    {currency.format(expenses)}
                                </Text>
                            </BarLabel>
                            <BarTrack>
                                <BarFill color={colors.dangerDark} width={barWidth(expenses, biggest)} />
                            </BarTrack>
                        </BarLine>
                    </Bars>
                </Column>

                <Breakdown>
                    <Line>
                        <Text paddingBottom={0}>Pago pelos clientes (online)</Text>
                        <Text paddingBottom={0} color={colors.nightDark}>{currency.format(paid)}</Text>
                    </Line>
                    <Line>
                        <Text paddingBottom={0}>Taxas retidas</Text>
                        <Text paddingBottom={0} color={colors.nightDark}>- {currency.format(fees)}</Text>
                    </Line>
                    {offline > 0 && (
                        <Line>
                            <Text paddingBottom={0}>Vendas presenciais (PDV)</Text>
                            <Text paddingBottom={0} color={colors.nightDark}>{currency.format(offline)}</Text>
                        </Line>
                    )}
                    <Line total>
                        <Text paddingBottom={0} color={colors.nightDark} type='bold'>Receita líquida</Text>
                        <Text paddingBottom={0} color={colors.nightDark} type='bold'>{currency.format(revenue)}</Text>
                    </Line>
                    <Line>
                        <Text paddingBottom={0}>Despesas pagas e pendentes</Text>
                        <Text paddingBottom={0} color={colors.nightDark}>- {currency.format(expenses)}</Text>
                    </Line>
                    <Line total>
                        <Text paddingBottom={0} color={outcome.color} type='bold'>Resultado</Text>
                        <Text paddingBottom={0} color={outcome.color} type='bold'>{currency.format(balance)}</Text>
                    </Line>
                </Breakdown>
            </Content>

            {revenue === 0 && (
                <Text>O evento ainda não tem vendas aprovadas, por isso o resultado é só o que já foi gasto.</Text>
            )}

            {budget > 0 && (
                <Text>
                    Orçamentos ({currency.format(budget)}) ainda não são gasto e ficam fora desta conta.
                </Text>
            )}
        </Card>
    );
}

EventResultCard.propTypes = {
    result: PropTypes.shape({
        revenue: PropTypes.shape({
            paid: PropTypes.number,
            fees: PropTypes.number,
            offline: PropTypes.number,
            total: PropTypes.number,
        }),
        expenses: PropTypes.number,
        balance: PropTypes.number,
        outcome: PropTypes.string,
        margin: PropTypes.number,
    }),
    budgetExpenses: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const Card = styled(Column)`
    width: 100%;
    padding: 15px;
    margin-top: 10px;
    gap: 16px;
    background: ${colors.neutralLight};
    border-radius: 6px;
    border: 1px solid #e5e7eb;
`;

const Content = styled(Column)`
    gap: 24px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: flex-start;

        & > * {
            flex: 1;
        }
    }
`;

const Headline = styled(Row)`
    align-items: center;
`;

const Amount = styled.p`
    margin: 4px 0 2px;
    font-size: 32px;
    font-weight: 700;
    line-height: 1.2;
    color: ${({ color }) => color};
`;

const Bars = styled(Column)`
    gap: 12px;
    margin-top: 16px;
`;

const BarLine = styled(Column)`
    gap: 6px;
`;

const BarLabel = styled(Row)`
    justify-content: space-between;
`;

const BarTrack = styled.div`
    width: 100%;
    height: 10px;
    border-radius: 5px;
    background: ${colors.neutralDark};
    overflow: hidden;
`;

const BarFill = styled.div`
    height: 100%;
    border-radius: 5px;
    width: ${({ width }) => width}%;
    background: ${({ color }) => color};
`;

const Breakdown = styled(Column)`
    gap: 10px;
`;

const Line = styled(Row)`
    justify-content: space-between;
    gap: 12px;
    ${({ total }) => total && `
        padding-top: 10px;
        border-top: 1px solid #e5e7eb;
    `}
`;
